import {createContext, useCallback, useContext} from 'react';
import {Outlet, useLocation, useParams} from 'react-router-dom';
import {type User} from 'firebase/auth';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {
  doc,
  getFirestore,
  type DocumentData,
  type DocumentReference,
} from 'firebase/firestore';
import {Authenticated} from 'components/auth/Authenticated';
import {ProfileProvider} from 'contexts/ProfileContext';
import {
  getCourseDocumentRef,
  type CourseDocument,
} from '@cip/core/firebase/client';
import type {CourseId} from 'types/common';
import {useAsyncData} from 'utils/hooks';
import {
  getApplicationDataRef,
  type ApplicationData,
  type ApplicationType,
} from '../firebase/models/applicationData';
import {getUserApplicationDataFirebasePath} from '../firebase/models/applicantData';
import {
  getApplicationTypeFromPath,
  getParentCourseId,
  type AllApplicationData,
} from '../general/utils/apputils';
import getAllApplicationData from '../general/utils/getAllApplicationData';
import {getUserAssignmentProgressMapDocumentReference} from 'assignments/models/userAssignmentProgressMapReferences';
import {getUserLessonsProgressMapRef} from 'course/lessons/userLessonsProgressMap';
import {useApplicationAutoAcceptEligible} from '../firebase/functions/useIsApplicationAutoAcceptEligible';
import {useSectionTeachingAttempts} from '../sectionLeaderApplication/sectionteaching/firebase/useSectionTeachingAttempts';

/**
 * The data shared by every page in an application flow served under
 * `/:courseId/join/:targetCourseId` (student) or
 * `/:courseId/applyteach/:targetCourseId` (section leader).
 *
 * It is loaded once by {@link ApplicationJoinLayout} — which is the flow's
 * authentication boundary — and kept mounted while the child pages (splash,
 * "About You", ...) swap, so navigating between them neither re-fetches these
 * documents nor flashes a full-page loader.
 */
export interface ApplicationJoinData {
  /** The course being applied to (the URL's `:targetCourseId`). */
  targetCourseId: CourseId;
  /**
   * Whether this is a student (`join`) or section-leader (`applyteach`)
   * application; selects which application-structure and applicant documents are
   * loaded.
   */
  applicationType: ApplicationType;
  /** The target course's document, or `undefined` until it has loaded. */
  courseData: CourseDocument | undefined;
  /** True while the course document is still loading. */
  courseDataLoading: boolean;
  /** The error from loading the course document, or `undefined` if none. */
  courseDataError: Error | undefined;
  /**
   * The course-wide application structure (form info, splash cards, about
   * sections, ...), or `undefined` until it has loaded. Shared by the splash and
   * "About You" pages.
   */
  appStructureData: ApplicationData | undefined;
  /** True while the application-structure document is still loading. */
  appStructureLoading: boolean;
  /** The error from loading the application structure, or `undefined` if none. */
  appStructureError: Error | undefined;
  /**
   * The signed-in user's raw saved answers for this application, or `undefined`
   * when nothing has been saved yet. Raw (unconverted) shape, suitable for
   * prefilling the About form.
   */
  applicantData: DocumentData | undefined;
  /** True while the applicant document is still loading. */
  applicantDataLoading: boolean;
  /** The error from loading the applicant document, or `undefined` if none. */
  applicantDataError: Error | undefined;
  /** Reference to the applicant document, for writes. */
  applicantDataRef: DocumentReference<DocumentData>;
  /**
   * The applicant's aggregated progress across the whole application (saved
   * answers, assignment and lesson progress, teaching-demo, ...), used to render
   * the splash checklist. This is the flow's most expensive fetch; loading it
   * here keeps it warm when returning to the splash from another page.
   *
   * Mostly a point-in-time batch read, with exceptions: `userAssignmentProgress`
   * and `lessonsProgress` are live subscriptions, because the tasks that write them
   * (the coding exercise, the slide lesson) run on routes outside this subtree and
   * would otherwise leave a completed task's badge blue until a reload (#4038).
   * `sectionTeachingComplete` is likewise live — the sim-teaching session runs
   * *inside* this subtree, so the layout never remounts to pick up a freshly
   * completed attempt, and the batch read alone left that badge stale too (#4191).
   */
  allApplicationData: AllApplicationData;
  /** True while the aggregated application data is still loading. */
  allApplicationDataLoading: boolean;
  /** The error from loading the aggregated application data, or `null` if none. */
  allApplicationDataError: Error | null;
  /**
   * Re-runs the one-shot batch read behind {@link allApplicationData}. Use this
   * after an action this subtree itself performs that the batch read cannot
   * observe live — e.g. the `record` teaching-demo video upload, which lands in
   * Storage rather than a Firestore doc and so has no subscription equivalent.
   */
  refetchAllApplicationData: () => Promise<void>;
  /**
   * Whether the signed-in user is on the course's auto-accept list for this
   * application. Checked once here and kept in state so it does not re-fetch (and
   * re-flash) on every return to the splash. It does not gate page rendering.
   */
  autoAcceptEligible: boolean;
  /** The error from the auto-accept eligibility check, or `null` if none. */
  autoAcceptEligibleError: Error | null;
}

const ApplicationJoinContext = createContext<ApplicationJoinData | null>(null);

/**
 * Reads the shared application-flow data provided by {@link ApplicationJoinLayout}.
 *
 * @returns The shared course, application-structure, applicant and aggregated
 *   application data for the current `join`/`applyteach` flow.
 * @throws If called from a component that is not rendered under an
 *   {@link ApplicationJoinLayout} route.
 */
export function useApplicationJoinData(): ApplicationJoinData {
  const context = useContext(ApplicationJoinContext);
  if (!context) {
    throw new Error(
      'useApplicationJoinData must be used within an <ApplicationJoinLayout> route',
    );
  }
  return context;
}

/**
 * Layout route for an application flow. It is the flow's authentication boundary
 * (via {@link Authenticated}), so every child page renders with a guaranteed
 * signed-in user and the shared course/theme/experiment providers, loaded once.
 *
 * @returns The authenticated data provider wrapping the child routes' `<Outlet/>`.
 */
export function ApplicationJoinLayout() {
  return <Authenticated component={ApplicationJoinDataProvider} />;
}

/**
 * Resolves the flow's route parameters, then defers to
 * {@link ApplicationJoinDataLoader} which loads the shared data. Split out so the
 * loader receives a guaranteed `targetCourseId` and runs its data hooks
 * unconditionally.
 *
 * Also wraps the flow in {@link ProfileProvider}, which the splash pages read
 * for the viewer's role. This flow is deliberately not a child of
 * `<CoursePage>` (see the comment in `CourseRoutes.tsx`), the only other place
 * that provider is mounted, so without it here `ProfileContext` would fall back
 * to its default `loading: true` and the splash gate would never clear.
 *
 * `CompletionProvider` is deliberately NOT mounted here. Nothing in this flow
 * reads completion state, but the provider opens eight per-course progress
 * listeners, which made the flow's initial Firestore burst large enough to stall
 * the whole page for ~30s on a cold WebKit load. Progress needed for the splash
 * checklist is fetched separately by `getAllApplicationData`, and the one write
 * the flow performs goes straight to `setSelfCheckCompletedForUser` from
 * `useAppStatusServerRecorder`. See
 * [#3697](https://github.com/CodeinPlace/cip/issues/3697).
 *
 * @param user - The signed-in user, supplied by {@link Authenticated}.
 * @returns The data loader for the resolved flow, or a bare `<Outlet/>` if the
 *   route somehow lacks a `:targetCourseId` (it always provides one).
 */
function ApplicationJoinDataProvider({user}: {user: User}) {
  const {targetCourseId} = useParams<{targetCourseId: string}>();
  const location = useLocation();
  const applicationType: ApplicationType =
    getApplicationTypeFromPath(location.pathname) ?? 'student';

  if (!targetCourseId) {
    return <Outlet />;
  }

  return (
    <ProfileProvider>
      {/* Keyed by flow identity: the student and sl routes both render
          this layout at the same tree position, so React reuses the
          loader instance when switching flows. Its Firestore hooks only
          re-subscribe after paint, which would briefly serve the previous
          flow's data with loading=false — the splash pages' type-guard
          narrowing then flashes their error page. The key remounts the
          loader instead, so every hook restarts at loading=true. Within
          one flow (splash ↔ About) the key is stable and nothing
          remounts or refetches. */}
      <ApplicationJoinDataLoader
        key={`${targetCourseId}/${applicationType}`}
        user={user}
        targetCourseId={targetCourseId}
        applicationType={applicationType}
      />
    </ProfileProvider>
  );
}

/**
 * Subscribes once to the flow's shared documents and aggregated application data,
 * exposes them through {@link useApplicationJoinData}, and renders the active
 * child page via `<Outlet/>`.
 *
 * @param user - The signed-in user.
 * @param targetCourseId - The course being applied to.
 * @param applicationType - Whether this is the student or section-leader flow.
 * @returns The context provider wrapping the child routes' `<Outlet/>`.
 */
function ApplicationJoinDataLoader({
  user,
  targetCourseId,
  applicationType,
}: {
  user: User;
  targetCourseId: CourseId;
  applicationType: ApplicationType;
}) {
  const [courseData, courseDataLoading, courseDataError] = useDocumentData(
    getCourseDocumentRef({courseId: targetCourseId}),
  );

  const [appStructureData, appStructureLoading, appStructureError] =
    useDocumentData(getApplicationDataRef(targetCourseId, applicationType));

  // Read raw (no converter): the About form prefills from raw applicant fields.
  const applicantDataRef = doc(
    getFirestore(),
    getUserApplicationDataFirebasePath({
      userId: user.uid,
      courseId: targetCourseId,
      applicationType,
    }),
  );
  const [applicantData, applicantDataLoading, applicantDataError] =
    useDocumentData(applicantDataRef);

  // `getAllApplicationData` needs `formInfo` from `appStructureData`, which is
  // still loading on mount. Calling it early with `formInfo: null` anyway would
  // fire a full batch of Firestore/Storage reads whose result is discarded the
  // instant `appStructureData` arrives and this hook re-runs for real, so this
  // wrapper holds the call until `formInfo` exists and only one real batch of
  // reads ever happens.
  //
  // Once the structure read has settled without producing `formInfo` — it
  // errored, or the course has no application-data document — waiting is no
  // longer correct: the splash pages check their loading flags BEFORE their
  // error flags, so a promise that never settles leaves them on the loading
  // page forever instead of showing the error. Reject in that case so the gate
  // falls through.
  const getAllApplicationDataOnceReady = useCallback(
    (params: Parameters<typeof getAllApplicationData>[0]) => {
      if (!params.formInfo) {
        return appStructureLoading
          ? new Promise<AllApplicationData>(() => {})
          : Promise.reject(
              new Error(
                `No application form info for ${targetCourseId} (${applicationType})`,
              ),
            );
      }
      return getAllApplicationData(params);
    },
    [appStructureLoading, targetCourseId, applicationType],
  );

  const [
    allApplicationData,
    allApplicationDataLoading,
    allApplicationDataError,
    refetchAllApplicationData,
  ] = useAsyncData({
    asyncFunction: getAllApplicationDataOnceReady,
    params: [
      {
        userId: user.uid,
        targetCourseId,
        applicationType,
        formInfo: appStructureData?.formInfo ?? null,
      },
    ],
  });

  const {
    isUserApplicationAutoAcceptEligible: autoAcceptEligible,
    error: autoAcceptEligibleError,
  } = useApplicationAutoAcceptEligible(applicationType);

  // `getAllApplicationData` above is a one-shot batch of getDoc reads, so on its
  // own it cannot see progress recorded after this layout mounted. That is fine
  // for the tasks hosted inside this subtree, but the coding exercise and the
  // slide lesson are completed on course-scoped routes elsewhere — their badges
  // stayed blue until the applicant reloaded the page (#4038). Subscribing to the
  // two documents those tasks write keeps the checklist honest.
  //
  // Application tasks are completed in the PARENT course, which is where
  // getAllApplicationData reads them from too — keep the two in lockstep.
  const progressCourseId = getParentCourseId(targetCourseId);
  const [liveAssignmentProgress] = useDocumentData(
    getUserAssignmentProgressMapDocumentReference({
      userId: user.uid,
      courseId: progressCourseId,
    }),
  );
  const [liveLessonsProgress] = useDocumentData(
    getUserLessonsProgressMapRef({
      userId: user.uid,
      courseId: progressCourseId,
    }),
  );

  // The sim-teaching session (unlike the coding exercise and slide lesson) runs
  // INSIDE this subtree — completing it never remounts ApplicationJoinLayout, so
  // the batch read's `sectionTeachingComplete` snapshot goes stale the moment an
  // attempt completes in an already-open checklist tab (#4191, deferred from
  // #4038). Subscribe live to the applicant's attempts instead. Passing
  // `undefined` for non-`sl` applicationTypes keeps the hook idle (empty,
  // non-loading) rather than opening an unused listener.
  const {
    attempts: liveSectionTeachingAttempts,
    loading: liveSectionTeachingAttemptsLoading,
    error: liveSectionTeachingAttemptsError,
  } = useSectionTeachingAttempts(
    applicationType === 'sl' ? user.uid : undefined,
    targetCourseId,
  );
  // `undefined` while the first snapshot is still in flight, or if the
  // subscription errored, so the merge below falls back to the batch read
  // instead of a premature/incorrect "no attempts" false. A transient
  // subscription error must never downgrade an applicant who the batch read
  // already found complete.
  const liveSectionTeachingComplete =
    applicationType === 'sl' &&
    !liveSectionTeachingAttemptsLoading &&
    !liveSectionTeachingAttemptsError
      ? liveSectionTeachingAttempts.some(
          attempt => attempt.status === 'completed',
        )
      : undefined;

  // Prefer the live value, falling back to the batch read's. A subscription that
  // has not delivered yet and a document that does not exist both surface as
  // `undefined` here, and the fallback is correct either way: the batch read
  // resolves the same document, so it is either the same data or also empty.
  const allApplicationDataWithLiveProgress: AllApplicationData =
    allApplicationData
      ? {
          ...allApplicationData,
          userAssignmentProgress:
            liveAssignmentProgress ?? allApplicationData.userAssignmentProgress,
          lessonsProgress:
            liveLessonsProgress ?? allApplicationData.lessonsProgress,
          sectionTeachingComplete:
            liveSectionTeachingComplete ??
            allApplicationData.sectionTeachingComplete,
        }
      : allApplicationData;

  const value: ApplicationJoinData = {
    targetCourseId,
    applicationType,
    courseData,
    courseDataLoading,
    courseDataError,
    appStructureData,
    appStructureLoading,
    appStructureError,
    applicantData,
    applicantDataLoading,
    applicantDataError,
    applicantDataRef,
    allApplicationData: allApplicationDataWithLiveProgress,
    allApplicationDataLoading,
    allApplicationDataError,
    refetchAllApplicationData,
    autoAcceptEligible,
    autoAcceptEligibleError,
  };

  return (
    <ApplicationJoinContext.Provider value={value}>
      <Outlet />
    </ApplicationJoinContext.Provider>
  );
}
